import { useEffect, useRef } from "react"
import { Platform } from "react-native"

import Constants from "expo-constants"

import { Alert } from "src/components/Alert"

// How often to check the deployed build manifest. Matches the SME Portal's
// service-worker update interval (10 minutes).
const CHECK_INTERVAL_MS = 1000 * 60 * 10

// Served from the web export root (the `public/` directory is copied verbatim).
const VERSION_URL = "/version.json"

/**
 * Web-only: detect when a newer build of the app has been deployed and prompt
 * the user to reload, mirroring the SME Portal's "New Version Available" dialog.
 *
 * How it works:
 *   - `scripts/generate-web-version.js` stamps each web build with a unique
 *     `buildId`, written to `public/version.json` and embedded into the bundle
 *     via `app.config.ts` (`extra.webBuildId`).
 *   - This hook periodically fetches the deployed `/version.json` (bypassing the
 *     cache). When the served `buildId` differs from the embedded one, a new
 *     build is live, so we surface a reload prompt.
 *
 * No-op on native and in development. Safe to call once at app startup.
 */
export function useWebAppUpdatePrompt() {
  // Persist across the component lifetime so we only prompt once per session.
  const promptedRef = useRef(false)

  useEffect(() => {
    if (Platform.OS !== "web" || __DEV__) return
    if (typeof window === "undefined" || typeof fetch === "undefined") return

    const embeddedBuildId = Constants.expoConfig?.extra?.webBuildId as string | undefined
    // Without an embedded build id we cannot reliably tell builds apart, so we
    // skip the check entirely rather than risk a false "update available".
    if (!embeddedBuildId) return

    let cancelled = false

    const checkForUpdate = async () => {
      if (cancelled || promptedRef.current) return
      try {
        const response = await fetch(`${VERSION_URL}?ts=${Date.now()}`, { cache: "no-store" })
        if (!response.ok) return

        const data = (await response.json()) as { buildId?: string }
        const latestBuildId = data?.buildId
        if (!latestBuildId || latestBuildId === embeddedBuildId) return

        if (promptedRef.current) return
        promptedRef.current = true

        Alert.alert(
          "New Version Available",
          "Sidekick has been updated. Please refresh the page to get the latest version.",
          [
            { text: "Later", style: "cancel" },
            { text: "Refresh", onPress: () => window.location.reload() },
          ],
        )
      } catch (error) {
        // Transient network/parse error — log and retry on the next tick.
        console.warn("[WEB UPDATE PROMPT] Failed to check for app update:", error)
      }
    }

    const interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS)

    // Also check when the tab regains focus, so a returning user sees the prompt
    // promptly instead of waiting out the full interval.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForUpdate()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    // Initial check shortly after load.
    checkForUpdate()

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [])
}
